import { useState, useMemo, useCallback, memo } from "react"
import {
  FileText,
  Search,
  Filter,
  X,
  Download,
  Archive,
  Trash2,
  FolderOpen,
  Paperclip,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
} from "lucide-react"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/Input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import { Checkbox } from "@/components/ui/Checkbox"
import { cn } from "src/utils/Functions"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import type { DemoDocument } from "@/types/demo.types"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import { DocumentActionsMenu } from "./DocumentActionsMenu"
import { WidgetCard } from "./WidgetCard"
import { getDocStatusVariant, getDocStatusLabel } from "../../utils"
import { DEFAULT_SORTING, DEFAULT_PAGINATION, PAGE_SIZE_OPTIONS } from "@/constants/deal.constants"

const STATUS_ITEMS = [
  { value: "all", label: "All Statuses" },
  { value: "signed", label: "Signed" },
  { value: "pending_review", label: "Pending Review" },
  { value: "draft", label: "Draft" },
]

const PAGE_SIZE_ITEMS = PAGE_SIZE_OPTIONS.map((size) => ({
  value: `${size}`,
  label: `${size}`,
}))

interface DocumentsTableProps {
  onUpload?: () => void
}

export const DocumentsTable = memo(({ onUpload }: DocumentsTableProps) => {
  const isDemo = useIsDemo()
  const { demoDocuments } = useDemoDealsDocuments()
  const documents = isDemo ? demoDocuments : []
  const [sorting, setSorting] = useState<SortingState>([...DEFAULT_SORTING])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const filteredDocuments = useMemo(() => {
    if (searchQuery === "" && statusFilter === "all") return documents

    const query = searchQuery.toLowerCase()
    return documents.filter(doc => {
      if (statusFilter !== "all" && doc.status !== statusFilter) return false
      if (query === "") return true
      return doc.name.toLowerCase().includes(query) ||
             doc.type.toLowerCase().includes(query)
    })
  }, [searchQuery, statusFilter, documents])

  const columns = useMemo<ColumnDef<DemoDocument, any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-3 max-w-[280px]">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted shrink-0">
              <FileText className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-foreground text-sm font-medium truncate" title={row.original.name}>{row.original.name}</span>
              {row.original.linkedAnalysis && (
                <span className="text-blue-400 text-xs flex items-center gap-1 shrink-0" title="Has linked analysis" aria-label="Has linked analysis">
                  <LinkIcon className="w-3 h-3" aria-hidden="true" />
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <span className="text-foreground/70 text-sm">{row.original.type}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge variant={getDocStatusVariant(row.original.status)} size="sm">
            {getDocStatusLabel(row.original.status)}
          </Badge>
        ),
      },
      {
        accessorKey: "size",
        header: "Size",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm tabular-nums">{row.original.size}</span>
        ),
      },
      {
        accessorKey: "uploadedAt",
        header: "Uploaded",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">{row.original.uploadedAt}</span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DocumentActionsMenu doc={row.original} />
          </div>
        ),
        enableSorting: false,
      },
    ],
    []
  )

  const table = useReactTable({
    data: filteredDocuments,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: DEFAULT_PAGINATION,
    },
  })

  const selectedCount = Object.keys(rowSelection).length

  const clearSelection = useCallback(() => setRowSelection({}), [])
  const clearFilters = useCallback(() => {
    setSearchQuery("")
    setStatusFilter("all")
  }, [])
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])
  const handleStatusChange = useCallback((v: string | null) => {
    if (v) setStatusFilter(v)
  }, [])

  return (
    <WidgetCard>
      <WidgetCard.Header className={cn("gap-3", selectedCount > 0 && "justify-start")}>
        {selectedCount > 0 ? (
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={clearSelection}
              aria-label="Cancel selection"
            >
              <X className="w-4 h-4" />
            </Button>
            <span className="text-foreground text-sm font-medium">{selectedCount} selected</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                Download
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                <Archive className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                Archive
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                Delete
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 shrink-0 pl-2">
              <FolderOpen className="text-muted-foreground size-5" aria-hidden="true" />
              <h3 className="text-foreground font-semibold">Documents</h3>
            </div>
            <div className="flex items-center gap-2 flex-1 justify-end">
              <div className="relative max-w-[200px]">
                <Input
                  startIcon={<Search className="w-3.5 h-3.5 text-muted-foreground pointer-events-none" />}
                  type="search"
                  placeholder="Search…"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  aria-label="Search documents"
                />
              </div>
              <Select items={STATUS_ITEMS} value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[140px] h-7 text-sm" aria-label="Filter by status">
                  <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" aria-hidden="true" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(searchQuery || statusFilter !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={clearFilters}
                  aria-label="Clear filters"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
              <span className="text-muted-foreground text-xs tabular-nums shrink-0">
                {filteredDocuments.length}{filteredDocuments.length !== documents.length && `/${documents.length}`}
              </span>
              {onUpload && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onUpload}>
                  <Paperclip className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                  Upload
                </Button>
              )}
            </div>
          </>
        )}
      </WidgetCard.Header>

      <WidgetCard.Content className="min-h-0">
        <div className="flex-1 overflow-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="text-muted-foreground text-sm text-left border-b border-border">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className={cn(
                      "pb-2 font-medium",
                      header.id === "select" && "w-10",
                      header.id === "actions" && "sticky right-0 bg-neutral-100 dark:bg-neutral-900"
                    )}>
                      {header.isPlaceholder ? null : (
                        header.column.getCanSort() ? (
                          <button
                            className="inline-flex items-center gap-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded"
                            onClick={header.column.getToggleSortingHandler()}
                            aria-label={`Sort by ${header.column.columnDef.header}${header.column.getIsSorted() ? `, currently ${header.column.getIsSorted() === 'asc' ? 'ascending' : 'descending'}` : ''}`}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getIsSorted() === 'asc' && (
                              <ArrowDown className="w-3.5 h-3.5" aria-hidden="true" />
                            )}
                            {header.column.getIsSorted() === 'desc' && (
                              <ArrowUp className="w-3.5 h-3.5" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        )
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  className={cn(
                    "border-b border-border/50 hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                    row.getIsSelected() && "bg-muted/30"
                  )}
                  aria-selected={row.getIsSelected()}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        "py-1.75",
                        cell.column.id === "actions" && "sticky right-0 bg-neutral-100 dark:bg-neutral-900"
                      )}
                      onClick={cell.column.id === "select" || cell.column.id === "actions" ? (e) => e.stopPropagation() : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <nav className="mt-4 flex items-center justify-between text-foreground/80 border-t border-border pt-4" aria-label="Documents table pagination">
          <div className="text-sm tabular-nums" aria-live="polite">
            {(() => {
              const pageIndex = table.getState().pagination.pageIndex
              const pageSize = table.getState().pagination.pageSize
              const total = table.getPrePaginationRowModel().rows.length
              const start = total === 0 ? 0 : pageIndex * pageSize + 1
              const end = Math.min(total, (pageIndex + 1) * pageSize)
              return `${start}–${end} of ${total} Documents`
            })()}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="docs-page-size" className="text-sm">Rows per page</label>
              <Select
                items={PAGE_SIZE_ITEMS}
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(v) => table.setPageSize(Number(v))}
              >
                <SelectTrigger id="docs-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="h-7"
              variant="secondary"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </Button>
            <span className="text-sm tabular-nums">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
            </span>
            <Button
              className="h-7"
              variant="secondary"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to next page"
            >
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </nav>
      </WidgetCard.Content>
    </WidgetCard>
  )
})

DocumentsTable.displayName = "DocumentsTable"
